/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, Stack, Skeleton, useStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import Breadcrumb from '../../../components/breadcrumb'
import EditorialComponentsFactory from '../../../components/editorial-components/editorial-components-factory'
import {remCalc} from '../../../theme/_utils'

const PageHeader = ({
    category,
    productSearchResult,
    isLoading,
    searchQuery,
    editorialComponents,
    ...otherProps
}) => {
    const styles = useStyleConfig('PageHeader')

    return (
        <Box {...otherProps} data-testid="sf-product-list-breadcrumb">
            {category && !searchQuery ? (
                <Breadcrumb categories={category.parentCategoryTree} />
            ) : (
                !category &&
                !searchQuery && (
                    <Skeleton my="small.base" h="small.lg" w={{base: '100%', md: remCalc(350)}} />
                )
            )}

            {editorialComponents &&
                editorialComponents.map((component) => (
                    <Box key={component.uid} {...styles.editorialComponentsContainer}>
                        <EditorialComponentsFactory
                            componentType={component?._content_type_uid}
                            {...component}
                        />
                    </Box>
                ))}

            {/* Category Title */}
            {category?.name ? (
                <Flex {...styles.container}>
                    <Text {...styles.title}>
                        {`${category?.name || ''} (${productSearchResult?.total ?? 0})`}{' '}
                    </Text>
                </Flex>
            ) : (
                !searchQuery && (
                    <Skeleton
                        my="small.base"
                        h={remCalc(32)}
                        w={{base: '100%', md: remCalc(350)}}
                    />
                )
            )}

            {/*Search Category Title */}
            {searchQuery && (
                <>
                    <Stack {...styles.searchContainer}>
                        <Stack {...styles.searchContainerTitles}>
                            <Flex {...styles.searchTitle}>
                                <Text>
                                    <FormattedMessage
                                        defaultMessage="Search Results for"
                                        id="product_list.search.title"
                                    />
                                    <b>{` ${searchQuery || ''}`}</b>
                                </Text>
                            </Flex>
                            <Flex {...styles.searchSubtitle}>
                                <Text>
                                    {!isLoading && productSearchResult?.total && (
                                        <>{productSearchResult?.total}</>
                                    )}
                                </Text>
                                <Text>
                                    <FormattedMessage
                                        defaultMessage="results for"
                                        id="product_list.subtitle"
                                    />
                                </Text>{' '}
                                <Text>
                                    <b>{`${searchQuery || ''}`}</b>
                                </Text>
                            </Flex>
                        </Stack>
                    </Stack>
                </>
            )}
        </Box>
    )
}

PageHeader.getTemplateName = () => 'page-header'

PageHeader.propTypes = {
    category: PropTypes.object,
    productSearchResult: PropTypes.object,
    editorialComponents: PropTypes.array,
    isLoading: PropTypes.bool,
    searchQuery: PropTypes.string,
}

export default PageHeader
